import {
  createStoreSlice as createSlice,
  type PayloadAction,
} from '@/store/core';
import { RootState } from '@/store';
import { StoreError } from '@/types/storeError.interface';
import { IApplication, IUserApplication, IndexedApplications } from '@/typings';

interface ApplicationState {
  loading: boolean;
  error: StoreError | null;
  applications: IndexedApplications;
  currentApplicationId: IApplication['id'] | null;
  userApplications: IUserApplication[];
}

type ApplicationSummary = {
  id: IApplication['id'];
  completedPercentage: number;
  completedSections: number;
  attestationSummary?: IApplication['attestationSummary'];
};

const initialState: ApplicationState = {
  applications: {},
  currentApplicationId: null,
  userApplications: [],
  loading: false,
  error: null,
};

export const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    applicationLoading: state => {
      state.loading = true;
    },
    fetchUserApplicationsSuccess: (
      state,
      action: PayloadAction<IUserApplication[]>,
    ) => {
      state.loading = false;
      state.error = null;
      state.userApplications = action.payload;
    },
    fetchApplicationSuccess: (state, action: PayloadAction<IApplication>) => {
      state.loading = false;
      state.error = null;
      state.currentApplicationId = action.payload.id;
      state.applications[action.payload.id] = {
        ...(state.applications?.[action.payload.id] ?? {}),
        ...action.payload,
      };
    },
    fetchApplicationSummarySuccess: (
      state,
      action: PayloadAction<ApplicationSummary>,
    ) => {
      state.loading = false;
      state.error = null;
      state.applications[action.payload.id] = {
        ...state.applications[action.payload.id],
        ...action.payload,
      };
    },
    fetchApplicationFailure: (state, action: PayloadAction<StoreError>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearUserApplications: state => {
      state.userApplications = [];
    },
  },
});

export const {
  applicationLoading,
  fetchUserApplicationsSuccess,
  fetchApplicationSuccess,
  fetchApplicationSummarySuccess,
  fetchApplicationFailure,
  clearUserApplications,
} = applicationSlice.actions;

export const selectApplication = (state: RootState) =>
  state[applicationSlice.name] as typeof initialState;
